import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../users/AuthContext';
import { createManagementAPI } from '../api/managementAPI'; // Import createManagementAPI

/**
 * Custom hook for managing users
 * Provides state management and CRUD operations for users
 */
export const useUsers = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const { userAPI } = createManagementAPI(makeAuthenticatedRequest); // Call createManagementAPI
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalUsers: 0
  });

  // Fetch users with pagination
  const fetchUsers = useCallback(async (page = 1, limit = 20) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userAPI.listUsers(page, limit);
      
      if (result.success) {
        setUsers(result.data);
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          pageSize: limit,
          totalUsers: result.data.length
        }));
      } else {
        setError(result.error);
        setUsers([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new user
  const createUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userAPI.createUser(userData);
      
      if (result.success) {
        // Refresh users list
        await fetchUsers(pagination.currentPage, pagination.pageSize);
        return { success: true, message: result.message };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const error = err.message || 'Failed to create user';
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, pagination.currentPage, pagination.pageSize]);

  // Search for user by email
  const findUserByEmail = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userAPI.findUserByEmail(email);
      return result;
    } catch (err) {
      const error = err.message || 'Failed to search user';
      setError(error);
      return { success: false, error, data: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Search for user by ID
  const findUserById = useCallback(async (uid) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userAPI.findUserById(uid);
      return result;
    } catch (err) {
      const error = err.message || 'Failed to search user';
      setError(error);
      return { success: false, error, data: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter users locally
  const filterUsers = useCallback((searchTerm) => {
    if (!searchTerm) return users;
    
    return users.filter(user => {
      const email = user.email || '';
      const uid = user.uid || '';
      const commonName = user.commonName || '';
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      
      const searchLower = searchTerm.toLowerCase();
      return email.toLowerCase().includes(searchLower) ||
             uid.toLowerCase().includes(searchLower) ||
             commonName.toLowerCase().includes(searchLower) ||
             firstName.toLowerCase().includes(searchLower) ||
             lastName.toLowerCase().includes(searchLower);
    });
  }, [users]);

  // Go to next page
  const nextPage = useCallback(() => {
    const nextPageNum = pagination.currentPage + 1;
    fetchUsers(nextPageNum, pagination.pageSize);
  }, [fetchUsers, pagination.currentPage, pagination.pageSize]);

  // Go to previous page
  const prevPage = useCallback(() => {
    const prevPageNum = Math.max(1, pagination.currentPage - 1);
    fetchUsers(prevPageNum, pagination.pageSize);
  }, [fetchUsers, pagination.currentPage, pagination.pageSize]);

  // Go to specific page
  const goToPage = useCallback((page) => {
    fetchUsers(page, pagination.pageSize);
  }, [fetchUsers, pagination.pageSize]);

  // Change page size
  const changePageSize = useCallback((newSize) => {
    fetchUsers(1, newSize);
  }, [fetchUsers]);

  // Initial load
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    pagination,
    fetchUsers,
    createUser,
    findUserByEmail,
    findUserById,
    filterUsers,
    nextPage,
    prevPage,
    goToPage,
    changePageSize,
    refresh: () => fetchUsers(pagination.currentPage, pagination.pageSize)
  };
};
